import { Language, LanguageCode } from '@/types/common';
import { LANGUAGE_MAP } from '@/utils/constants/language';

export const PAGE_LANGUAGES: Language[] = [
  { code: 'ko', name: '한국어', nameInKorean: '한국어', smallName: 'KO', type: ['asia'] },
  { code: 'en_US', name: 'English', nameInKorean: '영어', smallName: 'EN', type: ['america', 'asia', 'europe'] },
  { code: 'ja', name: '日本語', nameInKorean: '일본어', smallName: 'JA', type: ['asia'] },
  { code: 'zh', name: '中文 (简体)', nameInKorean: '중국어(간체)', smallName: 'ZH', type: ['asia'] },
  { code: 'es', name: 'Español', nameInKorean: '스페인어', smallName: 'ES', type: ['europe', 'america'] },
  { code: 'de', name: 'Deutsch', nameInKorean: '독일어', smallName: 'DE', type: ['europe'] },
  { code: 'ru', name: 'Русский', nameInKorean: '러시아어', smallName: 'RU', type: ['europe', 'asia'] },
  { code: 'fr', name: 'Français', nameInKorean: '프랑스어', smallName: 'FR', type: ['europe', 'africa', 'america'] },
  { code: 'it', name: 'Italiano', nameInKorean: '이탈리아어', smallName: 'IT', type: ['europe'] },
  { code: 'hi', name: 'हिंदी', nameInKorean: '힌디어', smallName: 'HI', type: ['asia'] },
  { code: 'vi', name: 'Tiếng Việt', nameInKorean: '베트남어', smallName: 'VI', type: ['asia'] },
  { code: 'id', name: 'Bahasa Indonesia', nameInKorean: '인도네시아어', smallName: 'ID', type: ['asia'] },
  { code: 'th', name: 'ภาษาไทย', nameInKorean: '태국어', smallName: 'TH', type: ['asia'] },
  { code: 'pt', name: 'Português', nameInKorean: '포르투갈어', smallName: 'PT', type: ['europe', 'america', 'africa'] },
  { code: 'tr', name: 'Türkçe', nameInKorean: '터키어', smallName: 'TR', type: ['europe', 'asia'] },
  { code: 'pl', name: 'Polski', nameInKorean: '폴란드어', smallName: 'PL', type: ['europe'] },
  { code: 'ar', name: 'العربية', nameInKorean: '아랍어', smallName: 'AR', type: ['asia', 'africa'] },
  { code: 'ms', name: 'Bahasa Melayu', nameInKorean: '말레이어', smallName: 'MS', type: ['asia'] },
  { code: 'nl', name: 'Nederlands', nameInKorean: '네덜란드어', smallName: 'NL', type: ['europe'] },
  { code: 'zh_TW', name: '中文 (繁體)', nameInKorean: '중국어(번체)', smallName: 'ZH-TW', type: ['asia'] },
];

const LANGUAGE_CODE_MAP = {
  ko: 'ko',
  'ko-kr': 'ko',
  en: 'en_US',
  'en-us': 'en_US',
  'en-gb': 'en_US',
  ja: 'ja',
  'ja-jp': 'ja',
  zh: 'zh',
  'zh-cn': 'zh',
  'zh-tw': 'zh_TW',
  de: 'de',
  'de-de': 'de',
  es: 'es',
  'es-es': 'es',
  ru: 'ru',
  'ru-ru': 'ru',
  fr: 'fr',
  'fr-fr': 'fr',
  sv: 'sv',
  'sv-se': 'sv',
  it: 'it',
  'it-it': 'it',
  hi: 'hi',
  'hi-in': 'hi',
  vi: 'vi',
  'vi-vn': 'vi',
  id: 'id',
  'id-id': 'id',
  th: 'th',
  'th-th': 'th',
  pt: 'pt',
  'pt-pt': 'pt',
  tr: 'tr',
  'tr-tr': 'tr',
  pl: 'pl',
  'pl-pl': 'pl',
  ar: 'ar',
  'ar-ar': 'ar',
  ms: 'ms',
  'ms-ms': 'ms',
  nl: 'nl',
  'nl-nl': 'nl',
  mn: 'mn',
  'mn-mn': 'mn',
} as const;

export const DATE_PICKER_LOCALE_MAP = {
  ko: 'ko',
  en_US: 'en',
  ja: 'ja',
  zh: 'zh',
  de: 'de',
  es: 'es',
  ru: 'ru',
  fr: 'fr',
  it: 'it',
  hi: 'hi',
  vi: 'vi',
  id: 'id',
  th: 'th',
  pt: 'pt',
  tr: 'tr',
  ms: 'tr',
  nl: 'nl',
  zh_TW: 'tw',
  mn: 'mn',
};

export const REGION_FILTERS = [
  { value: 'all', label: 'label_all' },
  { value: 'asia', label: 'label_asia' },
  { value: 'europe', label: 'label_europe' },
  { value: 'america', label: 'label_america' },
  { value: 'africa', label: 'label_africa' },
  // { value: 'etc', label: '그 외' },
];

export const getLanguageByCode = (languageCode: LanguageCode): Language => {
  const code = getNormalizedLanguageCode(languageCode);
  return LANGUAGE_MAP[code] ?? LANGUAGE_MAP['en_US'];
};

export const getBrowserLanguage = () => {
  const browserLanguage = navigator.language ?? 'ko';
  return getNormalizedLanguageCode(browserLanguage);
};

export const getNormalizedLanguageCode = (languageCode: string): LanguageCode => {
  const code = languageCode.replace(/_/g, '-').toLowerCase();
  return LANGUAGE_CODE_MAP[code] || 'en_US';
};

export const getLanguages = (languageCodes: LanguageCode[]): Language[] => {
  return languageCodes.map((lang) => LANGUAGE_MAP[lang]);
};
